import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import { format } from 'date-fns';

const { LuPhone, LuSearch, LuX, LuCheckCircle, LuClock, LuCar, LuUser, LuTrash2 } = LuIcons;

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    // Fetch leads and join with cars_for_sale
    const { data, error } = await supabase
      .from('leads')
      .select(`
        *,
        cars_for_sale (brand, model, year, price)
      `)
      .order('created_at', { ascending: false });
      
    if (!error && data) setLeads(data);
    setLoading(false);
  };

  const handleStatusChange = async (id, newStatus) => {
    await supabase.from('leads').update({ status: newStatus }).eq('id', id);
    fetchLeads();
  };

  const handleDelete = async (id) => {
    if(window.confirm('هل أنت متأكد من حذف هذا الطلب؟')) {
      await supabase.from('leads').delete().eq('id', id);
      fetchLeads();
    }
  };

  const filteredLeads = leads.filter(lead => 
    lead.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    lead.customer_phone.includes(searchTerm) ||
    (lead.marketer_code && lead.marketer_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" dir="rtl">
      <div className="p-4 sm:p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <SafeIcon icon={LuPhone} className="w-6 h-6 text-blue-600" />
            طلبات الشراء والعملاء
          </h2>
          <p className="text-sm text-slate-500 mt-1">إدارة الطلبات الواردة من الموقع أو من المسوقين</p>
        </div>
        
        <div className="relative w-full sm:w-64">
          <SafeIcon icon={LuSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="بحث بالاسم، الهاتف أو كود المسوق..."
            className="w-full pe-10 ps-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">العميل</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">السيارة المطلوبة</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">المسوق</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">التاريخ</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">الحالة</th>
              <th className="px-6 py-4 text-end text-xs font-black text-slate-500 uppercase">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan="6" className="text-center py-10 text-slate-400">جاري التحميل...</td></tr>
            ) : filteredLeads.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-10 text-slate-400">لا توجد طلبات.</td></tr>
            ) : (
              filteredLeads.map(lead => (
                <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">{lead.customer_name}</div>
                    <div className="text-sm text-slate-500 mt-1" dir="ltr">{lead.customer_phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    {lead.cars_for_sale ? (
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{lead.cars_for_sale.brand} {lead.cars_for_sale.model}</div>
                        <div className="text-xs text-slate-500">{lead.cars_for_sale.year}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-red-400">السيارة محذوفة</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {lead.marketer_code ? (
                      <span className="bg-blue-50 text-blue-700 font-bold px-2 py-1 rounded-lg text-xs border border-blue-100">
                        {lead.marketer_code}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">- بدون مسوق -</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {format(new Date(lead.created_at), 'yyyy-MM-dd HH:mm')}
                  </td>
                  <td className="px-6 py-4">
                    <select 
                      value={lead.status}
                      onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                      className={`text-xs font-bold px-2 py-1.5 rounded-lg outline-none cursor-pointer border ${
                        lead.status === 'new' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                        lead.status === 'contacted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}
                    >
                      <option value="new">🆕 جديد</option>
                      <option value="contacted">📞 تم التواصل</option>
                      <option value="closed">✅ تمت البيعة</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-end">
                    <button onClick={() => handleDelete(lead.id)} className="text-red-500 hover:text-red-700 p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                      <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminLeads;